import React, { useEffect, useState } from 'react';
import { Wallet, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { deferaCreditUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { REWARDS_SECTION_CARD } from '@/constants/brandColors';
import { CREDIT_ENTRY_LABELS } from '../../../supabase/functions/_shared/defera-credit-history.ts';

const formatUsdFromCents = (cents) =>
  `$${(Math.abs(Math.round(Number(cents) || 0)) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const COLLAPSED_COUNT = 5;

/**
 * The title and the line under it for one history row, as the balance
 * endpoint builds them from structured fields (defera-credit-history.ts).
 * Against an endpoint that predates `label`/`detail`, the title comes from the
 * entry type and the line is the trip code. Never `description`: that is the
 * team's internal note, and an older endpoint still sends it.
 */
function entryText(entry) {
  const title = entry.label || CREDIT_ENTRY_LABELS[entry.type] || 'Activity';
  if (typeof entry.detail === 'string') return { title, detail: entry.detail };
  return { title, detail: entry.trip_reference ? `Trip ${entry.trip_reference}` : '' };
}

function formatEntryDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * "Defera Credits" wallet card on My Profile. Shows the customer's house-credit
 * balance and its ledger so credit is visible before checkout, not discovered
 * there. Reads the same `defera-credit/balance` endpoint the checkout preview
 * uses; renders nothing for customers with no credit history.
 */
export default function DeferaCreditsSection() {
  const [balanceCents, setBalanceCents] = useState(0);
  const [ledger, setLedger] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch(deferaCreditUrl('balance'), {
          headers: mergeSupabaseEdgeHeaders({ 'Authorization': `Bearer ${session.access_token}` }),
          credentials: 'omit',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setBalanceCents(Number(data.balance_cents) || 0);
        // Only posted rows move the balance — pending/failed/reversed would
        // just confuse the running history.
        setLedger((data.ledger || []).filter((entry) => entry.status === 'posted'));
      } catch (e) {
        console.error('Failed to load Defera Credit balance:', e);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // No credit history → no card. Credits are granted (cancellation returns,
  // goodwill, birthdays), so an empty $0 wallet would only raise questions.
  if (!loaded || (balanceCents <= 0 && ledger.length === 0)) return null;

  const visibleLedger = showAll ? ledger : ledger.slice(0, COLLAPSED_COUNT);
  const hiddenCount = ledger.length - COLLAPSED_COUNT;

  return (
    <div className={REWARDS_SECTION_CARD}>
      <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-5 border-b border-white/[0.06]">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-emerald-500/[0.14] border border-emerald-400/25 flex items-center justify-center flex-shrink-0">
            <Wallet className="w-4 h-4 text-emerald-300" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white text-[15px] font-normal tracking-tight">Defera Credits</h3>
            <p className="text-white/60 text-[11px] font-normal">Applied automatically to your next booking</p>
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-6 space-y-4">
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3.5 flex items-center justify-between gap-3">
          <span className="text-[11px] uppercase tracking-widest text-emerald-200/70 font-normal">
            Available credit
          </span>
          <span className="text-xl font-light text-emerald-100 tabular-nums">
            {formatUsdFromCents(balanceCents)}
          </span>
        </div>
        <p className="text-[12.5px] text-white/60 font-normal leading-relaxed">
          Credit applies at checkout automatically — your deposit first, then upcoming
          payments — until it&rsquo;s used up. It never expires.
        </p>

        {ledger.length > 0 && (
          <div className="pt-1">
            <div className="text-[10px] text-white/40 font-normal uppercase tracking-widest mb-2.5">
              Credit history
            </div>
            <div className="space-y-2">
              {visibleLedger.map((entry) => {
                const cents = Number(entry.amount_cents) || 0;
                const isPositive = cents > 0;
                const { title, detail } = entryText(entry);
                return (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-brand-800/40 bg-brand-900/20 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-white text-[13px] font-normal tracking-tight">
                        {title}
                      </p>
                      {detail && (
                        <p className="text-white/40 text-[11px] font-normal break-words">
                          {detail}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={cn('text-[13px] font-normal tabular-nums', isPositive ? 'text-emerald-300' : 'text-white/85')}>
                        {isPositive ? '+' : '−'}{formatUsdFromCents(cents)}
                      </p>
                      <p className="text-white/35 text-[11px] font-normal">
                        {formatEntryDate(entry.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] font-normal text-white/50 hover:text-white/80 transition-colors"
              >
                {showAll ? (
                  <>Show less <ChevronUp className="w-3.5 h-3.5" /></>
                ) : (
                  <>Show all {ledger.length} entries <ChevronDown className="w-3.5 h-3.5" /></>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
